/**
 * canvas-engine/presets/decompositionAdapters/custom.js
 *
 * Custom_Code_Section family adapter. The Custom HTML and Custom CSS sections
 * are expressed through the canvas model the same way every other section is:
 *
 *   Custom HTML → a single editable `html` Canvas_Element whose `content.html`
 *                 carries the author's markup and `content.css` carries scoped
 *                 CSS.
 *   Custom CSS  → an `html` Canvas_Element carrying ONLY scoped CSS in
 *                 `content.css` (no markup) — i.e. Canvas_Element style data.
 *
 * The core engine's text-stack would also synthesize a heading text element
 * from `content.title`, which is not wanted here, so this adapter builds the
 * Canvas_Model directly. It reuses the core frame-style deriver for the section
 * frame and records lossless provenance.
 *
 * ZERO-REUSE CONSTRAINT: imports ONLY sibling engine modules. Pure,
 * deterministic. Never emits a `native` element.
 *
 * Requirements: 4.3, 5.1, 5.7
 */

import { REFERENCE_FRAME_WIDTH, deriveFrameStyle, parsePadding, htmlElement } from './buildSectionCanvas';
import { normalizeCanvas } from '../../model/canvasModel';
import { MIN_DIMENSION } from '../../model/schema';
import { CUSTOM_PRESETS } from '../presetData/customPresets';

function isPlainObject(v) {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function pickString(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function deepClone(v) {
  return JSON.parse(JSON.stringify(v == null ? null : v));
}

/** The Custom_Code_Section types this adapter handles. */
export const CUSTOM_TYPES = Object.freeze(Object.keys(CUSTOM_PRESETS));

/** True when `sectionType` is a Custom_Code_Section (custom HTML / CSS). */
export function handlesCustomType(sectionType) {
  const t = typeof sectionType === 'string' ? sectionType.trim() : '';
  return CUSTOM_TYPES.includes(t) || (t.length >= 6 && t.slice(0, 6) === 'custom');
}

/**
 * Resolve a custom `sectionType_or_data` argument into a concrete descriptor.
 * A descriptor is used as-is; a string is looked up in `CUSTOM_PRESETS` (the
 * `customHtml` starter is the fallback).
 *
 * @param {(string|object)} input a `CUSTOM_PRESETS` key or a custom descriptor
 * @param {object} opts `{ sectionType? }`
 * @returns {{ data: object, sectionType: string }}
 */
function resolveCustom(input, opts) {
  if (isPlainObject(input)) {
    const sectionType = pickString(opts.sectionType) || pickString(input.sectionType) || 'customHtml';
    return { data: input, sectionType };
  }
  const key = pickString(input) || pickString(opts.sectionType) || 'customHtml';
  const data = isPlainObject(CUSTOM_PRESETS[key]) ? CUSTOM_PRESETS[key] : (CUSTOM_PRESETS.customHtml || {});
  const sectionType = pickString(opts.sectionType) || pickString(data.sectionType) || key;
  return { data: isPlainObject(data) ? data : {}, sectionType };
}

/**
 * Decompose a Custom HTML / Custom CSS section into a normalized Canvas_Model
 * carrying a single editable `html` Canvas_Element.
 *
 * Accepts EITHER a transcribed custom descriptor OR a `CUSTOM_PRESETS` key
 * string (`customHtml` / `customCss`). Custom HTML → `content.html` (+ scoped
 * `content.css`); Custom CSS → `content.css` style data only (Req 4.3).
 *
 * @param {(string|object)} sectionTypeOrData a `CUSTOM_PRESETS` key or a
 *   transcribed custom preset DATA descriptor
 *   `{ sectionType, style?, layout?, content: { html?, css?, title? } }`
 * @param {object} [options] `{ frameWidth?, idPrefix?, sectionType? }`
 * @returns {object} a normalized Canvas_Model
 */
export function customToCanvas(sectionTypeOrData, options = {}) {
  const opts = isPlainObject(options) ? options : {};
  const { data, sectionType } = resolveCustom(sectionTypeOrData, opts);
  const content = isPlainObject(data.content) ? data.content : {};
  const style = isPlainObject(data.style) ? data.style : {};
  const layout = isPlainObject(data.layout) ? data.layout : {};

  const idPrefix = pickString(opts.idPrefix) || sectionType;
  const frameWidth = Number.isFinite(opts.frameWidth) && opts.frameWidth >= MIN_DIMENSION ? opts.frameWidth : REFERENCE_FRAME_WIDTH;

  const html = pickString(content.html);
  const css = pickString(content.css);

  const pad = parsePadding(style.padding);
  const innerWidth = Math.max(frameWidth - pad.left - pad.right, MIN_DIMENSION);
  const elementHeight = html ? 240 : 120;

  const element = htmlElement(
    `${idPrefix}-html-0`,
    { x: pad.left, y: pad.top, width: innerWidth, height: elementHeight },
    1,
    html,
    css,
  );

  const frameHeight = Math.max(pad.top + elementHeight + pad.bottom, MIN_DIMENSION);
  const frameStyle = deriveFrameStyle(style);

  return normalizeCanvas({
    width: frameWidth,
    height: frameHeight,
    ...frameStyle,
    elements: [element],
    source: {
      type: sectionType,
      preserved: { content: deepClone(content), style: deepClone(style), layout: deepClone(layout) },
    },
  });
}

export default customToCanvas;
